import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { salesAPI, servicesAPI, productsAPI, clientsAPI, barbersAPI, appointmentsAPI, loyaltyAPI, cashRegisterAPI } from '../lib/api';
import { formatCurrency, cn, getErrorMessage, cleanPayload } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import SearchableSelect from '../components/SearchableSelect';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2, 
  User,
  CreditCard,
  Banknote,
  ArrowLeftRight,
  Search,
  Scissors,
  Package,
  Receipt,
  Check,
  Gift,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import printerService from '../lib/printerService';
import { buildSaleReceipt } from '../lib/escpos';

export default function POSPage() {
  const { selectedBusiness } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const appointmentIdParam = searchParams.get('appointment');
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('services');
  
  // Cart state
  const [cart, setCart] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [discount, setDiscount] = useState(0);
  const [usePoints, setUsePoints] = useState(0);

  // Mixed payment state: per-method amounts. Only the keys with > 0 are sent.
  const [mixedAmounts, setMixedAmounts] = useState({ cash: 0, card: 0, transfer: 0 });
  const [tipAmount, setTipAmount] = useState(0);
  
  // Checkout dialog
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [linkedAppointmentId, setLinkedAppointmentId] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  // Loyalty reward alert: raised when the selected client has an available
  // (earned but not yet redeemed) 50%-off or free-service reward, so the
  // cashier gets a visible reminder to apply the discount.
  const [loyaltyAlert, setLoyaltyAlert] = useState(null); // {rewardType, cardId, clientName} or null
  const [loyaltyDismissedForClient, setLoyaltyDismissedForClient] = useState(null);
  // Referral coupon alert — popped when a referred client with an active
  // 10 % discount coupon is selected in the POS. Dismiss-once semantics.
  const [referralAlert, setReferralAlert] = useState(null);
  const [referralDismissedForClient, setReferralDismissedForClient] = useState(null);

  // Track whether the business has a cash register open. Sales are blocked
  // both server-side and client-side when there isn't one — cashiers must
  // open the register from /caja first.
  const [hasOpenRegister, setHasOpenRegister] = useState(true); // optimistic default while we fetch
  const [registerAlertOpen, setRegisterAlertOpen] = useState(false);

  const refreshRegisterStatus = useCallback(async () => {
    if (!selectedBusiness) return;
    try {
      const { data } = await cashRegisterAPI.getAll();
      const isOpen = Array.isArray(data) && data.some((r) => r.status === 'open');
      setHasOpenRegister(isOpen);
    } catch {
      // On failure, be permissive on the frontend — the backend will still
      // reject the sale with 400 if the register isn't actually open.
      setHasOpenRegister(true);
    }
  }, [selectedBusiness]);

  useEffect(() => { refreshRegisterStatus(); }, [refreshRegisterStatus]);

  // ── Draft persistence for appointment-linked POS sessions ──────────────
  // When the cashier opens an appointment in the POS, adds extra services or
  // products, and navigates away WITHOUT completing the sale, we snapshot the
  // cart in sessionStorage keyed by appointment id. Next time they click
  // "Cobrar" on the same appointment the extras come back automatically.
  //
  // Snapshot is cleared on successful sale or explicit clear cart.
  // sessionStorage (not localStorage) so the draft dies with the browser tab —
  // safer than surviving indefinitely and confusing future days.
  const _draftKey = (aptId) => `pos_draft_apt_${aptId}`;
  const loadAppointmentDraft = (aptId) => {
    try {
      const raw = sessionStorage.getItem(_draftKey(aptId));
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  };
  const saveAppointmentDraft = (aptId, payload) => {
    try { sessionStorage.setItem(_draftKey(aptId), JSON.stringify(payload)); } catch (_) { /* quota */ }
  };
  const deleteAppointmentDraft = (aptId) => {
    try { sessionStorage.removeItem(_draftKey(aptId)); } catch (_) { /* no-op */ }
  };

  const loadData = useCallback(async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const [servicesRes, productsRes, clientsRes, barbersRes] = await Promise.all([
        servicesAPI.getAll(),
        productsAPI.getAll({ is_for_sale: true }),
        clientsAPI.getAll({ limit: 2000 }),
        barbersAPI.getAll({ include_pos_shared: true })
      ]);
      
      setServices(servicesRes.data);
      setProducts(productsRes.data);
      setClients(clientsRes.data);
      setBarbers(barbersRes.data);
    } catch (error) {
      logger.error('Error loading POS data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Inline "new client" creation from the POS searcher
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientData, setNewClientData] = useState({ first_name: '', last_name: '', phone: '' });
  const [creatingClient, setCreatingClient] = useState(false);

  const handleCreateClientInline = async () => {
    const first_name = (newClientData.first_name || '').trim();
    const last_name = (newClientData.last_name || '').trim();
    const phone = (newClientData.phone || '').trim();
    if (!first_name || !last_name) {
      toast.error('Nombre y apellido son obligatorios');
      return;
    }
    setCreatingClient(true);
    try {
      const payload = cleanPayload({
        business_id: selectedBusiness.id,
        first_name,
        last_name,
        phone,
      });
      const { data: created } = await clientsAPI.create(payload);
      setClients((prev) => [created, ...prev]);
      setSelectedClient(created);
      setNewClientOpen(false);
      setNewClientData({ first_name: '', last_name: '', phone: '' });
      toast.success(`Cliente "${created.first_name}" creado y seleccionado`);
    } catch (error) {
      logger.error('Error creating client:', error);
      toast.error(getErrorMessage(error, 'No se pudo crear el cliente'));
    } finally {
      setCreatingClient(false);
    }
  };


  // Preload appointment data when navigated from /agenda "Pagar" button
  useEffect(() => {
    if (!appointmentIdParam || loading) return;
    if (!services.length && !clients.length && !barbers.length) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: apt } = await appointmentsAPI.get(appointmentIdParam);
        if (cancelled || !apt) return;

        // Guard against cross-appointment draft corruption: if we're switching
        // from a previously linked appointment, unlink first so the save-cart
        // effect doesn't overwrite the previous draft with the new cart while
        // the transition is happening.
        setLinkedAppointmentId(null);

        // Draft-first: if the user previously added extras on this same
        // appointment and left without completing the sale, restore them
        // instead of rebuilding the cart from the appointment services.
        const draft = loadAppointmentDraft(appointmentIdParam);
        const aptBarberId = apt.barber_id || null;
        if (draft && Array.isArray(draft.cart) && draft.cart.length > 0) {
          setCart(draft.cart);
          if (draft.discount != null) setDiscount(draft.discount);
          if (draft.tipAmount != null) setTipAmount(draft.tipAmount);
          if (draft.paymentMethod) setPaymentMethod(draft.paymentMethod);
        } else {
          // Pre-fill cart with appointment services (with appointment barber assigned)
          const items = (apt.service_ids || []).map((sid) => {
            const svc = services.find((s) => s.id === sid);
            if (!svc) return null;
            return {
              type: 'service',
              item_id: svc.id,
              name: svc.name,
              price: svc.price,
              originalPrice: svc.price,
              quantity: 1,
              discount: 0,
              barber_id: aptBarberId,
            };
          }).filter(Boolean);
          if (items.length) setCart(items);
        }

        if (apt.client_id) {
          let c = clients.find((cl) => cl.id === apt.client_id);
          // If client isn't in the loaded list (huge db), fetch by id
          if (!c) {
            try {
              const { data: fetched } = await clientsAPI.get(apt.client_id);
              c = fetched;
              if (fetched) setClients((prev) => [fetched, ...prev]);
            } catch (e) {
              logger.error('Could not fetch appointment client:', e);
            }
          }
          if (c) setSelectedClient(c);
        } else if (apt.client_phone || apt.client_name) {
          // Walk-in appointment: try to match an existing client by phone (last 10 digits) or by exact name
          const phoneTail = (apt.client_phone || '').replace(/\D/g, '').slice(-10);
          const name = (apt.client_name || '').trim().toLowerCase();
          const match = clients.find((cl) => {
            const clPhoneTail = (cl.phone || '').replace(/\D/g, '').slice(-10);
            if (phoneTail && clPhoneTail && phoneTail === clPhoneTail) return true;
            const full = `${cl.first_name || ''} ${cl.last_name || ''}`.trim().toLowerCase();
            return name && full === name;
          });
          if (match) {
            setSelectedClient(match);
          } else {
            // Create a transient client object to display the name in the cart header
            setSelectedClient({
              id: null,
              first_name: apt.client_name || 'Cliente',
              last_name: '',
              phone: apt.client_phone || '',
              loyalty_points: 0,
              loyalty_level: 'bronze',
              _walkin: true,
            });
          }
        }
        setLinkedAppointmentId(apt.id);
        const clientLabel = apt.client_name ? `de ${apt.client_name}` : 'sin cliente registrado';
        toast.success(`Cita ${clientLabel} cargada`);
        // Clean URL so refresh doesn't re-trigger preload
        searchParams.delete('appointment');
        setSearchParams(searchParams, { replace: true });
      } catch (err) {
        logger.error('Error preloading appointment:', err);
        toast.error('No se pudo cargar la cita');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentIdParam, loading, services, clients, barbers]);

  // Persist the working cart while linked to an appointment. Any add/remove/
  // discount/tip/payment-method change gets snapshotted so the cashier never
  // loses extras added between opening the POS and completing the sale.
  useEffect(() => {
    if (!linkedAppointmentId) return;
    if (!cart || cart.length === 0) {
      // If the cart was emptied while linked, drop the draft (fresh start).
      deleteAppointmentDraft(linkedAppointmentId);
      return;
    }
    saveAppointmentDraft(linkedAppointmentId, {
      cart,
      discount,
      tipAmount,
      paymentMethod,
      updated_at: new Date().toISOString(),
    });
  }, [cart, discount, tipAmount, paymentMethod, linkedAppointmentId]);

  // Loyalty alert — whenever the cashier picks a client, check if the client
  // is enrolled in the loyalty program and has an earned-but-not-redeemed
  // reward on their active card. If so, raise a pop-up so the cashier
  // remembers to apply the discount to this sale.
  //
  // Dismiss-once semantics: after the cashier acknowledges the alert for a
  // particular client id, we don't re-open it if they briefly re-select the
  // same client. Selecting a different client resets the dismiss flag.
  useEffect(() => {
    if (!selectedClient?.id) {
      setLoyaltyAlert(null);
      return;
    }
    if (loyaltyDismissedForClient === selectedClient.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await loyaltyAPI.getCards(selectedClient.id);
        if (cancelled) return;
        const cards = (data && data.cards) || [];
        const active = cards.find((c) => c.status === 'active' || c.status === 'completed');
        if (!active) return;
        const has50 = active.reward_50_earned_at && !active.reward_50_redeemed_at;
        const hasFree = active.reward_free_earned_at && !active.reward_free_redeemed_at;
        if (!has50 && !hasFree) return;
        setLoyaltyAlert({
          cardId: active.id,
          clientName: `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim() || 'Cliente',
          // Prefer the higher-value reward if both are available
          rewardType: hasFree ? 'free' : '50',
          hasBoth: !!(has50 && hasFree),
          stamps: active.stamps || 0,
          maxStamps: active.max_stamps || 10,
        });
      } catch (err) {
        // Non-enrolled clients yield 404 or empty — silently ignore.
        logger.error('Loyalty alert check failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClient, loyaltyDismissedForClient]);

  // Referral coupon detection — surface the "¡Cliente Referido!" alert the
  // moment the cashier picks a client whose referral coupon is still active.
  useEffect(() => {
    if (!selectedClient?.id) { setReferralAlert(null); return; }
    if (referralDismissedForClient === selectedClient.id) return;
    if (!selectedClient.referral_coupon_active) { setReferralAlert(null); return; }
    setReferralAlert({
      clientId: selectedClient.id,
      clientName: `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim() || 'Cliente',
      percent: Number(selectedClient.referral_coupon_percent || 10),
      referrerName: selectedClient.referred_by_name || null,
    });
  }, [selectedClient, referralDismissedForClient]);

  // Auto-apply the coupon: sets the cart's global discount to the exact
  // 10 % of the current subtotal so cashier only needs to confirm.
  const applyReferralCoupon = () => {
    if (!referralAlert) return;
    const rawSubtotal = cart.reduce(
      (s, it) => s + Number(it.price || 0) * Number(it.quantity || 1),
      0,
    );
    const discountAmount = Math.round((rawSubtotal * (referralAlert.percent / 100)) * 100) / 100;
    setDiscount(discountAmount);
    setReferralDismissedForClient(referralAlert.clientId);
    setReferralAlert(null);
    toast.success(`Cupón aplicado: ${referralAlert.percent}% de descuento (${formatCurrency(discountAmount)})`);
  };

  const addToCart = (item, type) => {
    const existingIndex = cart.findIndex(
      (cartItem) => cartItem.item_id === item.id && cartItem.type === type
    );

    if (existingIndex > -1) {
      const updated = [...cart];
      updated[existingIndex].quantity += 1;
      setCart(updated);
    } else {
      setCart([
        ...cart,
        {
          type,
          item_id: item.id,
          name: item.name,
          price: item.price,
          originalPrice: item.price,
          quantity: 1,
          discount: 0,
          barber_id: null,  // per-line barber override
        }
      ]);
    }
    toast.success(`${item.name} agregado`);
  };

  const updateQuantity = (index, delta) => {
    const updated = [...cart];
    updated[index].quantity += delta;
    if (updated[index].quantity <= 0) {
      updated.splice(index, 1);
    }
    setCart(updated);
  };

  const updatePrice = (index, newPrice) => {
    const value = newPrice === '' ? 0 : parseFloat(newPrice);
    if (Number.isNaN(value) || value < 0) return;
    const updated = [...cart];
    updated[index].price = value;
    setCart(updated);
  };

  const updateItemBarber = (index, barberId) => {
    const updated = [...cart];
    updated[index].barber_id = barberId || null;
    setCart(updated);
  };

  const removeFromCart = (index) => {
    const updated = [...cart];
    updated.splice(index, 1);
    setCart(updated);
  };

  const clearCart = () => {
    if (linkedAppointmentId) {
      deleteAppointmentDraft(linkedAppointmentId);
    }
    setCart([]);
    setSelectedClient(null);
    setDiscount(0);
    setUsePoints(0);
    setPaymentMethod('cash');
    setMixedAmounts({ cash: 0, card: 0, transfer: 0 });
    setTipAmount(0);
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity) - item.discount, 0);
  const pointsValue = usePoints * 0.1; // 1 point = $0.10
  const total = Math.max(0, subtotal - discount - pointsValue);

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    // Block the sale when there is no open cash register. Backend enforces
    // this too, but showing the modal first avoids partial work (opens the
    // checkout dialog, etc.) and gives the cashier a clear next action.
    await refreshRegisterStatus();
    if (!hasOpenRegister) {
      setRegisterAlertOpen(true);
      return;
    }

    // Ensure every service has a barber assigned (commissions depend on it)
    const serviceWithoutBarber = cart.find(
      (item) => item.type === 'service' && !item.barber_id
    );
    if (serviceWithoutBarber) {
      toast.error(`Selecciona un barbero para "${serviceWithoutBarber.name}"`);
      return;
    }

    // Mixed payment validation: sum of parts must equal the total
    let mixedDetails = null;
    if (paymentMethod === 'mixed') {
      const entries = Object.entries(mixedAmounts).filter(([, v]) => Number(v) > 0);
      if (entries.length < 2) {
        toast.error('Pago mixto: ingresa monto en al menos 2 métodos');
        return;
      }
      const sum = entries.reduce((acc, [, v]) => acc + Number(v), 0);
      if (Math.abs(sum - total) > 0.01) {
        toast.error(
          `La suma (${formatCurrency(sum)}) no coincide con el total (${formatCurrency(total)})`
        );
        return;
      }
      mixedDetails = Object.fromEntries(entries.map(([k, v]) => [k, Number(v)]));
    }

    setProcessing(true);
    try {
      const saleData = {
        business_id: selectedBusiness.id,
        client_id: selectedClient?.id || null,
        client_name: selectedClient
          ? `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim() || null
          : null,
        barber_id: null,
        appointment_id: linkedAppointmentId || null,
        items: cart.map(item => ({
          type: item.type,
          item_id: item.item_id,
          quantity: item.quantity,
          discount: item.discount,
          unit_price: item.price,
          barber_id: item.barber_id || null
        })),
        discount,
        payment_method: paymentMethod,
        payment_details: mixedDetails,
        tip_amount: Number(tipAmount) || 0,
        loyalty_points_used: usePoints
      };

      const response = await salesAPI.create(saleData);
      setLastSale(response.data);

      // Snapshot client name for the receipt before clearing cart state
      const receiptClientName = selectedClient
        ? `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim()
        : '';

      // If sale came from an agenda appointment, mark it as completed
      if (linkedAppointmentId) {
        try {
          await appointmentsAPI.update(linkedAppointmentId, { status: 'completed' });
        } catch (e) {
          logger.error('Could not update appointment status:', e);
        }
        // Sale succeeded → the draft is no longer needed. Remove it before
        // we clear linkedAppointmentId so the delete uses the right key.
        deleteAppointmentDraft(linkedAppointmentId);
        setLinkedAppointmentId(null);
      }
      setCheckoutOpen(false);
      setTicketOpen(true);
      clearCart();
      toast.success('Venta completada');

      // Auto-print thermal ticket (best-effort, silent failure)
      const autoPrint = (() => {
        try {
          return localStorage.getItem('thermal_printer_autoprint') !== 'false';
        } catch {
          return true;
        }
      })();
      if (autoPrint && printerService.getSavedFingerprint()) {
        try {
          if (!printerService.isConnected()) {
            await printerService.reconnectPrinter();
          }
          const bytes = buildSaleReceipt({
            business: selectedBusiness,
            sale: response.data,
            clientName: receiptClientName,
          });
          await printerService.printRaw(bytes);
        } catch (printErr) {
          logger.error('Auto-print failed:', printErr);
          toast.error('No se pudo imprimir: ' + (printErr?.message || ''));
        }
      }
    } catch (error) {
      logger.error('Error creating sale:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar la venta');
    } finally {
      setProcessing(false);
    }
  };

  const filteredServices = services.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const serviceCategories = [...new Set(services.map(s => s.category).filter(Boolean))];
  const productCategories = [...new Set(products.map(p => p.category).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-7rem)] flex gap-6 animate-fade-in" data-testid="pos-page">
      {/* Products/Services Grid */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Search & Tabs */}
        <div className="mb-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar servicios o productos..."
              className="pl-10 bg-white border-slate-200"
              data-testid="pos-search"
            />
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="services" className="data-[state=active]:bg-white data-[state=active]:text-black">
                <Scissors className="w-4 h-4 mr-2" />
                Servicios
              </TabsTrigger>
              <TabsTrigger value="products" className="data-[state=active]:bg-white data-[state=active]:text-black">
                <Package className="w-4 h-4 mr-2" />
                Productos
              </TabsTrigger>
            </TabsList>

            <TabsContent value="services" className="mt-4">
              <ScrollArea className="h-[calc(100vh-18rem)]">
                {serviceCategories.length > 0 ? (
                  serviceCategories.map(category => (
                    <div key={category} className="mb-6">
                      <h3 className="text-sm font-medium text-slate-500 mb-3">{category}</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {filteredServices
                          .filter(s => s.category === category)
                          .map(service => (
                            <Card
                              key={service.id}
                              className="border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition-all card-hover"
                              onClick={() => addToCart(service, 'service')}
                              data-testid={`service-${service.id}`}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <Scissors className="w-5 h-5 text-slate-500" />
                                  {service.loyalty_points > 0 && (
                                    <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">
                                      +{service.loyalty_points} pts
                                    </Badge>
                                  )}
                                </div>
                                <h4 className="font-medium text-sm truncate">{service.name}</h4>
                                <p className="text-lg font-bold mt-1" style={{ fontFamily: 'Manrope' }}>
                                  {formatCurrency(service.price)}
                                </p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {service.duration_minutes} min
                                </p>
                              </CardContent>
                            </Card>
                          ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {filteredServices.map(service => (
                      <Card
                        key={service.id}
                        className="border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition-all"
                        onClick={() => addToCart(service, 'service')}
                      >
                        <CardContent className="p-4">
                          <Scissors className="w-5 h-5 text-slate-500 mb-2" />
                          <h4 className="font-medium text-sm truncate">{service.name}</h4>
                          <p className="text-lg font-bold mt-1">{formatCurrency(service.price)}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="products" className="mt-4">
              <ScrollArea className="h-[calc(100vh-18rem)]">
                {productCategories.length > 0 ? (
                  productCategories.map(category => (
                    <div key={category} className="mb-6">
                      <h3 className="text-sm font-medium text-slate-500 mb-3">{category}</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {filteredProducts
                          .filter(p => p.category === category)
                          .map(product => (
                            <Card
                              key={product.id}
                              className={cn(
                                "border-slate-200 bg-white cursor-pointer transition-all card-hover",
                                product.stock <= 0 && "opacity-50 cursor-not-allowed"
                              )}
                              onClick={() => product.stock > 0 && addToCart(product, 'product')}
                              data-testid={`product-${product.id}`}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <Package className="w-5 h-5 text-slate-500" />
                                  <Badge 
                                    variant="outline" 
                                    className={cn(
                                      "text-[10px]",
                                      product.stock <= product.min_stock 
                                        ? "text-amber-400 border-amber-500/30" 
                                        : "text-slate-500 border-slate-200"
                                    )}
                                  >
                                    Stock: {product.stock}
                                  </Badge>
                                </div>
                                <h4 className="font-medium text-sm truncate">{product.name}</h4>
                                <p className="text-lg font-bold mt-1" style={{ fontFamily: 'Manrope' }}>
                                  {formatCurrency(product.price)}
                                </p>
                              </CardContent>
                            </Card>
                          ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {filteredProducts.map(product => (
                      <Card
                        key={product.id}
                        className="border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition-all"
                        onClick={() => product.stock > 0 && addToCart(product, 'product')}
                      >
                        <CardContent className="p-4">
                          <Package className="w-5 h-5 text-slate-500 mb-2" />
                          <h4 className="font-medium text-sm truncate">{product.name}</h4>
                          <p className="text-lg font-bold mt-1">{formatCurrency(product.price)}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Cart Sidebar */}
      <Card className="w-96 flex-shrink-0 border-slate-200 bg-white flex flex-col">
        <CardHeader className="border-b border-slate-200 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <ShoppingCart className="w-5 h-5" />
              Carrito
            </CardTitle>
            {cart.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearCart}
                className="text-slate-500 hover:text-red-400"
              >
                Limpiar
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col p-4 overflow-hidden">
          {/* Client & Barber Selection */}
          <div className="space-y-3 mb-4">
            {/* Banner showing the appointment client (visible while linked to an appointment) */}
            {selectedClient && (selectedClient._walkin || linkedAppointmentId) && (
              <div
                className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm"
                data-testid="pos-appointment-client-banner"
              >
                <div className="flex items-center gap-2 text-emerald-800">
                  <User className="w-4 h-4" />
                  <div>
                    <div className="font-semibold leading-tight">
                      {`${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim() || 'Cliente'}
                    </div>
                    {selectedClient.phone && (
                      <div className="text-xs text-emerald-700/80 leading-tight">
                        {selectedClient.phone}
                      </div>
                    )}
                  </div>
                </div>
                <Badge className="bg-emerald-600 text-white border-0 text-[10px]">Cita</Badge>
              </div>
            )}

            <SearchableSelect
              items={clients}
              value={selectedClient?.id || ''}
              onChange={(c) => setSelectedClient(c)}
              getKey={(c) => c.id}
              getLabel={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Cliente'}
              getSubLabel={(c) => {
                const parts = [];
                if (c.phone) parts.push(c.phone);
                if ((c.loyalty_points || 0) > 0) parts.push(`${c.loyalty_points} pts`);
                return parts.join(' · ');
              }}
              searchFields={(c) => [c.first_name, c.last_name, c.phone, c.email]}
              placeholder={
                selectedClient && !selectedClient.id
                  ? `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim() || 'Cliente walk-in'
                  : 'Buscar cliente…'
              }
              emptyLabel="Sin clientes que coincidan"
              testId="pos-select-client"
              footer={({ close }) => (
                <button
                  type="button"
                  onClick={() => {
                    close();
                    setNewClientOpen(true);
                  }}
                  className="flex items-center justify-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
                  data-testid="pos-new-client-btn"
                >
                  <Plus className="w-4 h-4" />
                  Nuevo cliente
                </button>
              )}
            />
          </div>

          {/* Cart Items */}
          <ScrollArea className="flex-1 -mx-4 px-4">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                <ShoppingCart className="w-12 h-12 mb-3 opacity-50" />
                <p>Carrito vacío</p>
                <p className="text-sm">Agrega servicios o productos</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((item, index) => (
                  <div 
                    key={`${item.type}-${item.item_id}-${index}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 animate-fade-in"
                  >
                    <div className="w-10 h-10 rounded-md bg-slate-200 flex items-center justify-center flex-shrink-0">
                      {item.type === 'service' ? (
                        <Scissors className="w-5 h-5" />
                      ) : (
                        <Package className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.name}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-xs text-slate-400">$</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={item.price === 0 ? '' : item.price}
                          onChange={(e) => updatePrice(index, e.target.value)}
                          className="h-7 w-24 text-sm px-2 bg-white"
                          data-testid={`cart-price-${index}`}
                          aria-label="Precio unitario"
                        />
                        {typeof item.originalPrice === 'number' && item.originalPrice !== item.price && (
                          <span
                            className="text-[10px] text-amber-600"
                            title={`Precio original: ${formatCurrency(item.originalPrice)}`}
                          >
                            ✎ modificado
                          </span>
                        )}
                      </div>
                      <Select
                        value={item.barber_id || ''}
                        onValueChange={(value) => updateItemBarber(index, value)}
                      >
                        <SelectTrigger
                          className="h-7 mt-1 text-xs bg-white border-slate-200"
                          data-testid={`cart-barber-${index}`}
                        >
                          <SelectValue placeholder="Selecciona barbero…" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          {barbers.filter((b) => b.is_active !== false).map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.display_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-7 w-7 border-slate-200"
                        onClick={() => updateQuantity(index, -1)}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-7 w-7 border-slate-200"
                        onClick={() => updateQuantity(index, 1)}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 text-slate-500 hover:text-red-400"
                        onClick={() => removeFromCart(index)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Totals & Checkout */}
          {cart.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
              {/* Points */}
              {selectedClient && selectedClient.loyalty_points > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500 flex items-center gap-1">
                    <Gift className="w-4 h-4 text-amber-400" />
                    Puntos disponibles: {selectedClient.loyalty_points}
                  </span>
                  <Input
                    type="number"
                    value={usePoints}
                    onChange={(e) => setUsePoints(Math.min(parseInt(e.target.value) || 0, selectedClient.loyalty_points))}
                    className="w-20 h-8 bg-slate-100 border-slate-200 text-right"
                    max={selectedClient.loyalty_points}
                    min={0}
                  />
                </div>
              )}

              {/* Discount */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Descuento</span>
                <Input
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                  className="w-24 h-8 bg-slate-100 border-slate-200 text-right"
                  min={0}
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>

              {(discount > 0 || usePoints > 0) && (
                <div className="flex items-center justify-between text-sm text-emerald-400">
                  <span>Descuentos</span>
                  <span>-{formatCurrency(discount + pointsValue)}</span>
                </div>
              )}

              <div className="flex items-center justify-between text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>

              <Button 
                className="w-full h-12 bg-primary text-white hover:bg-primary/90 font-semibold"
                onClick={async () => {
                  // Guard the "Cobrar" flow at the earliest possible point so
                  // the cashier doesn't fill payment details before the block.
                  await refreshRegisterStatus();
                  if (!hasOpenRegister) { setRegisterAlertOpen(true); return; }
                  setCheckoutOpen(true);
                }}
                data-testid="checkout-btn"
              >
                <Receipt className="w-5 h-5 mr-2" />
                Cobrar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loyalty reward alert — reminds the cashier that the selected client
          has an earned reward waiting to be applied. */}
      <Dialog
        open={!!loyaltyAlert}
        onOpenChange={(open) => {
          if (!open && loyaltyAlert && selectedClient?.id) {
            setLoyaltyDismissedForClient(selectedClient.id);
          }
          if (!open) setLoyaltyAlert(null);
        }}
      >
        <DialogContent className="bg-white border-amber-200 max-w-md" data-testid="loyalty-reward-alert">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-3">
              <Gift className="w-7 h-7 text-amber-600" />
            </div>
            <DialogTitle className="text-center text-xl" style={{ fontFamily: 'Manrope' }}>
              {loyaltyAlert?.rewardType === 'free'
                ? '¡Servicio GRATIS disponible!'
                : '¡Descuento del 50% disponible!'}
            </DialogTitle>
          </DialogHeader>
          <div className="text-center space-y-3 py-2">
            <p className="text-slate-700">
              <strong>{loyaltyAlert?.clientName}</strong> pertenece al programa de lealtad
              y tiene una recompensa lista para canjear en esta venta.
            </p>
            {loyaltyAlert?.hasBoth && (
              <p className="text-sm text-slate-500">
                (Además tiene disponible el descuento del 50%.)
              </p>
            )}
            <div className="text-xs text-slate-400">
              Sellos: {loyaltyAlert?.stamps || 0} / {loyaltyAlert?.maxStamps || 10}
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              {loyaltyAlert?.rewardType === 'free'
                ? 'Aplica el descuento manual del 100% al servicio principal antes de cerrar la venta.'
                : 'Aplica el descuento manual del 50% al total antes de cerrar la venta.'}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => {
                if (selectedClient?.id) setLoyaltyDismissedForClient(selectedClient.id);
                setLoyaltyAlert(null);
              }}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="loyalty-alert-ok-btn"
            >
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Método de pago</DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="grid grid-cols-2 gap-3" data-testid="pos-payment-methods">
              {[
                { id: 'cash', label: 'Efectivo', Icon: Banknote },
                { id: 'card', label: 'Tarjeta', Icon: CreditCard },
                { id: 'transfer', label: 'Transferencia', Icon: ArrowLeftRight },
                { id: 'mixed', label: 'Mixto', Icon: ArrowLeftRight },
              ].map(({ id, label, Icon }) => (
                <Button
                  key={id}
                  variant="outline"
                  className={cn(
                    "h-20 flex-col border-slate-200",
                    paymentMethod === id && "border-primary bg-primary/5"
                  )}
                  onClick={() => {
                    setPaymentMethod(id);
                    if (id !== 'mixed') {
                      setMixedAmounts({ cash: 0, card: 0, transfer: 0 });
                    }
                    if (id === 'cash') {
                      setTipAmount(0);
                    }
                  }}
                  data-testid={`pos-pay-${id}`}
                >
                  <Icon className="w-6 h-6 mb-2" />
                  {label}
                </Button>
              ))}
            </div>

            {paymentMethod === 'mixed' && (
              <div
                className="p-4 rounded-lg border border-slate-200 bg-slate-50 space-y-3"
                data-testid="mixed-payment-form"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">Dividir el cobro</p>
                  <p className="text-xs text-slate-500">
                    Ingresa el monto en cada método que se vaya a usar
                  </p>
                </div>

                {[
                  { key: 'cash', label: 'Efectivo', Icon: Banknote },
                  { key: 'card', label: 'Tarjeta', Icon: CreditCard },
                  { key: 'transfer', label: 'Transferencia', Icon: ArrowLeftRight },
                ].map(({ key, label, Icon }) => (
                  <div key={key} className="flex items-center gap-3">
                    <div className="flex items-center gap-2 w-36 text-sm text-slate-700">
                      <Icon className="w-4 h-4 text-slate-500" />
                      {label}
                    </div>
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={mixedAmounts[key] || ''}
                        onChange={(e) =>
                          setMixedAmounts((prev) => ({
                            ...prev,
                            [key]: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0,
                          }))
                        }
                        placeholder="0.00"
                        className="pl-7 bg-white"
                        data-testid={`mixed-amount-${key}`}
                      />
                    </div>
                  </div>
                ))}

                {(() => {
                  const sum = Object.values(mixedAmounts).reduce((a, b) => a + Number(b || 0), 0);
                  const diff = total - sum;
                  return (
                    <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                      <span className="text-sm text-slate-600">
                        Suma:{' '}
                        <span className={cn('font-semibold', Math.abs(diff) < 0.01 ? 'text-emerald-600' : 'text-slate-800')}>
                          {formatCurrency(sum)}
                        </span>{' '}
                        / {formatCurrency(total)}
                      </span>
                      <span
                        className={cn(
                          'text-xs font-medium',
                          Math.abs(diff) < 0.01
                            ? 'text-emerald-600'
                            : diff > 0
                              ? 'text-amber-600'
                              : 'text-red-500'
                        )}
                        data-testid="mixed-diff"
                      >
                        {Math.abs(diff) < 0.01
                          ? 'OK'
                          : diff > 0
                            ? `Falta ${formatCurrency(diff)}`
                            : `Sobra ${formatCurrency(-diff)}`}
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}

            {(paymentMethod === 'card' || paymentMethod === 'transfer' || paymentMethod === 'mixed') && (
              <div
                className="p-4 rounded-lg border border-emerald-200 bg-emerald-50/40"
                data-testid="tip-form"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Propina</p>
                    <p className="text-xs text-slate-500">100% para el barbero, no se descuentan comisiones</p>
                  </div>
                  <div className="relative w-40">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={tipAmount || ''}
                      onChange={(e) =>
                        setTipAmount(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)
                      }
                      placeholder="0.00"
                      className="pl-7 bg-white text-right"
                      data-testid="tip-input"
                    />
                  </div>
                </div>
                {tipAmount > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[20, 40, 60, 100].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setTipAmount(preset)}
                        className="px-2.5 py-1 text-xs rounded-full bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-colors"
                      >
                        +${preset}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setTipAmount(0)}
                      className="px-2.5 py-1 text-xs rounded-full bg-white border border-slate-200 text-slate-500 hover:bg-slate-100 transition-colors"
                    >
                      Quitar
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="p-4 rounded-lg bg-slate-50">
              <div className="flex items-center justify-between text-sm text-slate-500">
                <span>Subtotal venta</span>
                <span>{formatCurrency(total)}</span>
              </div>
              {Number(tipAmount) > 0 && (
                <div className="flex items-center justify-between text-sm text-emerald-600 mt-1">
                  <span>Propina</span>
                  <span>+ {formatCurrency(tipAmount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-2xl font-bold mt-2" style={{ fontFamily: 'Manrope' }}>
                <span>Total a cobrar</span>
                <span>{formatCurrency(total + (Number(tipAmount) || 0))}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button 
              onClick={handleCheckout} 
              className="bg-primary text-white hover:bg-primary/90"
              disabled={processing}
              data-testid="confirm-payment-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirmar pago
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Dialog */}
      <Dialog open={ticketOpen} onOpenChange={setTicketOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center" style={{ fontFamily: 'Manrope' }}>
              <Check className="w-12 h-12 mx-auto mb-2 text-emerald-400" />
              Venta completada
            </DialogTitle>
          </DialogHeader>

          {lastSale && (
            <div className="py-4 space-y-4">
              <div className="p-4 rounded-lg bg-slate-50 font-mono text-sm">
                <div className="text-center mb-4">
                  <p className="font-bold text-lg">{selectedBusiness?.name}</p>
                  <p className="text-slate-500">Ticket #{lastSale.ticket_number}</p>
                </div>
                
                <div className="border-t border-dashed border-slate-200 py-3 space-y-2">
                  {lastSale.items.map((item) => (
                    <div key={item.id || `${item.name}-${item.quantity}`} className="flex justify-between">
                      <span className="truncate flex-1">{item.quantity}x {item.name}</span>
                      <span>{formatCurrency(item.subtotal)}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-dashed border-slate-200 py-3 space-y-1">
                  {lastSale.discount > 0 && (
                    <div className="flex justify-between text-emerald-400">
                      <span>Descuento</span>
                      <span>-{formatCurrency(lastSale.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg">
                    <span>TOTAL</span>
                    <span>{formatCurrency(lastSale.total)}</span>
                  </div>
                </div>

                {lastSale.loyalty_points_earned > 0 && (
                  <div className="text-center text-amber-400 text-xs mt-2">
                    +{lastSale.loyalty_points_earned} puntos de lealtad
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex-col gap-2">
            <Button 
              variant="outline" 
              className="w-full border-slate-200"
              onClick={async () => {
                if (!lastSale) return;
                if (!printerService.getSavedFingerprint()) {
                  toast.error('Conecta una impresora en Configuración → Impresora');
                  return;
                }
                try {
                  if (!printerService.isConnected()) {
                    await printerService.reconnectPrinter();
                  }
                  const bytes = buildSaleReceipt({
                    business: selectedBusiness,
                    sale: lastSale,
                    clientName: lastSale.client_name || '',
                  });
                  await printerService.printRaw(bytes);
                  toast.success('Ticket enviado a la impresora');
                } catch (err) {
                  toast.error(err?.message || 'No se pudo imprimir');
                }
              }}
              data-testid="pos-print-ticket-btn"
            >
              Imprimir ticket
            </Button>
            <Button 
              className="w-full bg-primary text-white hover:bg-primary/90"
              onClick={() => setTicketOpen(false)}
            >
              Nueva venta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline new-client dialog */}
      <Dialog open={newClientOpen} onOpenChange={setNewClientOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nuevo cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Nombre *</Label>
              <Input
                value={newClientData.first_name}
                onChange={(e) => setNewClientData((p) => ({ ...p, first_name: e.target.value }))}
                placeholder="Ej. Roberto"
                className="bg-white"
                data-testid="new-client-first-name"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-sm">Apellido</Label>
              <Input
                value={newClientData.last_name}
                onChange={(e) => setNewClientData((p) => ({ ...p, last_name: e.target.value }))}
                placeholder="Ej. Hernández"
                className="bg-white"
                data-testid="new-client-last-name"
              />
            </div>
            <div>
              <Label className="text-sm">Teléfono</Label>
              <Input
                value={newClientData.phone}
                onChange={(e) => setNewClientData((p) => ({ ...p, phone: e.target.value }))}
                placeholder="55 1234 5678"
                className="bg-white"
                data-testid="new-client-phone"
                inputMode="tel"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewClientOpen(false)}
              className="border-slate-200"
              disabled={creatingClient}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateClientInline}
              disabled={creatingClient}
              data-testid="new-client-save"
            >
              {creatingClient ? 'Creando…' : 'Crear y seleccionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Referral coupon alert — pops when cashier selects a referred client
          whose 10 % coupon has not been redeemed yet */}
      <Dialog open={!!referralAlert} onOpenChange={(o) => !o && setReferralAlert(null)}>
        <DialogContent
          className="bg-white max-w-md border-2 border-amber-400"
          data-testid="pos-referral-alert-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-900">
              <Gift className="w-5 h-5 text-amber-600" />
              ¡Cliente referido!
            </DialogTitle>
          </DialogHeader>
          {referralAlert && (
            <div className="space-y-3 py-2">
              <div className="rounded-lg bg-amber-50 border border-amber-300 p-4">
                <p className="text-sm text-amber-900">
                  <strong>{referralAlert.clientName}</strong> tiene un{' '}
                  <strong className="text-lg">{referralAlert.percent}% de descuento</strong> disponible
                  en su próximo servicio.
                </p>
                {referralAlert.referrerName && (
                  <p className="text-xs text-amber-800 mt-2">
                    Referido por: <strong>{referralAlert.referrerName}</strong>
                    {' — '}recibirá su sello cuando el cupón se canjee.
                  </p>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Al aplicar, se descuenta automáticamente el {referralAlert.percent}% del subtotal
                actual del carrito. El cupón se marca como usado al confirmar la venta.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (referralAlert) setReferralDismissedForClient(referralAlert.clientId);
                setReferralAlert(null);
              }}
              className="border-slate-200"
              data-testid="pos-referral-dismiss-btn"
            >
              Más tarde
            </Button>
            <Button
              onClick={applyReferralCoupon}
              disabled={cart.length === 0}
              className="bg-amber-600 text-white hover:bg-amber-700"
              data-testid="pos-referral-apply-btn"
            >
              Aplicar {referralAlert?.percent || 10}% de descuento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blocking modal — POS refuses to charge without an open cash register */}
      <Dialog open={registerAlertOpen} onOpenChange={setRegisterAlertOpen}>
        <DialogContent
          className="bg-white max-w-md border-2 border-red-300"
          data-testid="pos-no-register-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Caja del día sin abrir
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-900">
              No puedes registrar una venta hasta que abras la caja del día.
              El monto de apertura y todos los movimientos quedan trazados
              contra esa caja abierta.
            </div>
            <p className="text-xs text-slate-500">
              Ve a <strong>Caja</strong>, ingresa el monto de apertura y regresa a este POS.
              Tu carrito se mantiene intacto.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setRegisterAlertOpen(false)}
              className="border-slate-200"
              data-testid="pos-no-register-close-btn"
            >
              Cerrar
            </Button>
            <Button
              onClick={() => { setRegisterAlertOpen(false); navigate('/caja'); }}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="pos-no-register-go-btn"
            >
              Ir a Caja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
